// Parsec Cloud (https://parsec.cloud) Copyright (c) AGPL-3.0-only 2016-present Scille SAS

import { isWeb } from '@/parsec';
import { EnvironmentType, I18n } from 'megashark-lib';

// Returns the `X.Y` part of a version (`v3.5.1-a.0+dev` -> `3.5`), or `undefined` if it can't be parsed
export function getMajorMinorVersion(version: string): string | undefined {
  const match = version.trim().match(/^v?(\d+)\.(\d+)/);
  return match ? `${match[1]}.${match[2]}` : undefined;
}

export const APP_VERSION = __APP_VERSION__;
export const APP_MAJOR_MINOR_VERSION = getMajorMinorVersion(APP_VERSION);

/*
 STRIPE
*/

const STRIPE_ENV_VARIABLE = 'PARSEC_APP_STRIPE_API_KEY';
// cspell:disable-next-line
const DEFAULT_STRIPE_API_KEY = 'pk_test_P4dfuyoLBQtDHKjTiNDH3JH700TT3mCLbE';
const STRIPE_DISABLE_ENV_VARIABLE = 'PARSEC_APP_DISABLE_STRIPE';

function getStripeApiKey(): { key: string; mode: EnvironmentType } {
  if (import.meta.env[STRIPE_ENV_VARIABLE]) {
    return {
      key: import.meta.env[STRIPE_ENV_VARIABLE],
      mode: (import.meta.env[STRIPE_ENV_VARIABLE] as string).startsWith('pk_live_')
        ? EnvironmentType.Production
        : EnvironmentType.Development,
    };
  }
  return { key: DEFAULT_STRIPE_API_KEY, mode: EnvironmentType.Development };
}

function isStripeDisabled(): boolean {
  return import.meta.env[STRIPE_DISABLE_ENV_VARIABLE] === 'true' || (window as any).TESTING_DISABLE_STRIPE === true;
}

/*
 SENTRY
*/

const SENTRY_DISABLE_ENV_VARIABLE = 'PARSEC_APP_DISABLE_SENTRY';

function isSentryDisabled(): boolean {
  return import.meta.env[SENTRY_DISABLE_ENV_VARIABLE] === 'true';
}

/*
 BMS
*/

const BMS_ENV_VARIABLE = 'PARSEC_APP_BMS_API_URL';
const DEFAULT_BMS_URL = 'https://bms-dev.parsec.cloud';

function getBmsUrl(): string {
  if (import.meta.env[BMS_ENV_VARIABLE]) {
    return import.meta.env[BMS_ENV_VARIABLE];
  }
  return DEFAULT_BMS_URL;
}

/*
 PARSEC SIGN
*/

const SIGN_ENV_VARIABLE = 'PARSEC_APP_SIGN_URL';
const DEFAULT_SIGN_URL = 'https://sign-dev.parsec.cloud';

function getSignUrl(): string {
  if (import.meta.env[SIGN_ENV_VARIABLE]) {
    return import.meta.env[SIGN_ENV_VARIABLE];
  }
  return DEFAULT_SIGN_URL;
}

/*
 DEFAULT SERVERS
*/

const SAAS_SERVERS_ENV_VARIABLE = 'PARSEC_APP_SAAS_SERVERS';
const TRIAL_SERVERS_ENV_VARIABLE = 'PARSEC_APP_TRIAL_SERVERS';

const DEFAULT_SAAS_SERVERS = ['saas-v3.parsec.cloud', 'saas-demo-v3-mightyfairy.parsec.cloud'];
const DEFAULT_TRIAL_SERVERS = ['trial.parsec.cloud'];

function getSaasServers(): Array<string> {
  if ((window as any).TESTING_SAAS_SERVERS) {
    return (window as any).TESTING_SAAS_SERVERS.split(';');
  }
  if (import.meta.env[SAAS_SERVERS_ENV_VARIABLE]) {
    return import.meta.env[SAAS_SERVERS_ENV_VARIABLE].split(';');
  }
  return DEFAULT_SAAS_SERVERS;
}

function getTrialServers(): Array<string> {
  if ((window as any).TESTING_TRIAL_SERVERS) {
    return (window as any).TESTING_TRIAL_SERVERS.split(';');
  }
  if (import.meta.env[TRIAL_SERVERS_ENV_VARIABLE]) {
    return import.meta.env[TRIAL_SERVERS_ENV_VARIABLE].split(';');
  }
  return DEFAULT_TRIAL_SERVERS;
}

/*
 Parsec Auth
*/

const ACCOUNT_DEFAULT_SERVER = 'saas-v3.parsec.cloud';
const ACCOUNT_SERVER_ENV_VARIABLE = 'PARSEC_APP_ACCOUNT_SERVER';
const ENABLE_ACCOUNT_ENV_VARIABLE = 'PARSEC_APP_ENABLE_ACCOUNT';
const ENABLE_ACCOUNT_AUTO_LOGIN_ENV_VARIABLE = 'PARSEC_APP_ENABLE_ACCOUNT_AUTO_LOGIN';

function getAccountServer(): string {
  let addr = ACCOUNT_DEFAULT_SERVER;

  if ((window as any).TESTING_ACCOUNT_SERVER) {
    addr = (window as any).TESTING_ACCOUNT_SERVER;
  } else if (import.meta.env[ACCOUNT_SERVER_ENV_VARIABLE]) {
    addr = import.meta.env[ACCOUNT_SERVER_ENV_VARIABLE];
  } else if (isWeb()) {
    addr = document.location.hostname;
  } else {
    addr = ACCOUNT_DEFAULT_SERVER;
  }
  // replace whatever scheme with parsec3
  addr = addr.replace(/^([a-z][a-z0-9\-.+]*:\/\/)/i, 'parsec3://');
  // if it doesn't start with a scheme, we add parsec3://
  if (!/^[a-z][a-z0-9-.+]*:\/\//i.test(addr)) {
    addr = `parsec3://${addr}`;
  }

  return addr;
}

function isAccountEnabled(): boolean {
  return import.meta.env[ENABLE_ACCOUNT_ENV_VARIABLE] === 'true' || (window as any).TESTING_ENABLE_ACCOUNT === true;
}

function isAccountAutoLoginEnabled(): boolean {
  return (
    isAccountEnabled() &&
    (import.meta.env[ENABLE_ACCOUNT_AUTO_LOGIN_ENV_VARIABLE] === 'true' || (window as any).TESTING_ACCOUNT_AUTO_LOGIN === true)
  );
}

/*
 Custom branding
*/

const CUSTOM_BRANDING_ENV_VARIABLE = 'PARSEC_APP_ENABLE_CUSTOM_BRANDING';

function isCustomBrandingEnabled(): [boolean, string] {
  // On non-web platforms, custom branding is enabled/disabled at build time
  // (and always without cache busting suffix). The rational being that:
  // - Unlike for web, the meta flag in `index.html` cannot easily be modified
  //   in the released version of Parsec.
  // - It is no big deal to have the application always looking for custom
  //   branding files in a config folder at startup.
  // - We don't always enable custom branding on non-web platform since we
  //   want to exclude it from hardened build (see CSPN build).
  if ((!isWeb() && import.meta.env[CUSTOM_BRANDING_ENV_VARIABLE] === 'true') || (window as any).TESTING_ENABLE_CUSTOM_BRANDING === true) {
    return [true, ''];
  }

  const obj = document.querySelector('meta[name="custom-branding"]') as HTMLMetaElement | null;
  // Allowed values:
  // - `false`: Custom branding is disabled
  // - `true,cacheBustingSuffix=...`: Custom branding is enabled, note the mandatory cache
  //   busting suffix parameter that will be added to the resources names (e.g. using
  //   `cacheBustingSuffix=-xxx` means `custom_en-US-xxx.json` will be fetched).
  if (obj === null || obj.content === 'false') {
    return [false, ''];
  }
  const expectedPrefix = 'true,cacheBustingSuffix=';
  if (!obj.content.startsWith(expectedPrefix)) {
    throw Error(
      `Invalid value for \`meta[name="custom-branding"]\`, expected \`false\` or \`${expectedPrefix}...\`, got \`${obj.content}\``,
    );
  }
  const suffix = obj.content.substring(expectedPrefix.length);
  return [true, suffix];
}

/*
 * Shamir
 */
const SHAMIR_ENV_VARIABLE = 'PARSEC_APP_ENABLE_SHAMIR';

function isShamirEnabled(): boolean {
  return import.meta.env[SHAMIR_ENV_VARIABLE] === 'true' || (window as any).TESTING_ENABLE_SHAMIR === true;
}

/*
 Links
*/

function normalizeAppVersion(version: string): string {
  version = version.trim();
  if (version.startsWith('v')) {
    return version;
  }
  return `v${version}`;
}

const CLEAN_APP_VERSION = normalizeAppVersion(
  `${APP_VERSION.slice(0, APP_VERSION.indexOf('+') === -1 ? undefined : APP_VERSION.indexOf('+'))}`,
);

async function openUrl(url: string): Promise<void> {
  window.nativeAPI.log('debug', `Opening ${url}`);
  window.open(url, '_blank');
}

type Pages =
  | 'troubleshooting'
  | 'introduction'
  | 'installation'
  | 'new_organization'
  | 'first_steps'
  | 'manage_organization'
  | 'join_organization'
  | 'parsec_workspaces'
  | 'manage_files'
  | 'manage_devices'
  | 'security'
  | 'mfa';

function getDocumentationUserGuideLink(page: Pages, anchor = ''): string {
  return I18n.translate({ key: 'MenuPage.documentationGuideLink', data: { version: CLEAN_APP_VERSION, page: page, anchor: anchor } });
}

async function openDocumentationUserGuideLink(page: Pages, anchor = ''): Promise<void> {
  await openUrl(getDocumentationUserGuideLink(page, anchor));
}

async function openDocumentationLink(): Promise<void> {
  await openUrl(I18n.translate({ key: 'MenuPage.documentationLink', data: { version: CLEAN_APP_VERSION } }));
}

async function openSequesterDocumentationLink(): Promise<void> {
  await openUrl(I18n.translate({ key: 'CreateOrganization.sequester.link', data: { version: CLEAN_APP_VERSION } }));
}

async function openDownloadParsecLink(): Promise<void> {
  await openUrl(I18n.translate({ key: 'MenuPage.downloadParsecLink' }));
}

async function openContactLink(): Promise<void> {
  await openUrl(I18n.translate({ key: 'MenuPage.contactLink', data: { signUrl: getSignUrl() } }));
}

async function openLicenseLink(): Promise<void> {
  await openUrl(I18n.translate({ key: 'app.licenseLink', data: { version: CLEAN_APP_VERSION } }));
}

async function openChangelogLink(version?: string): Promise<void> {
  await openUrl(I18n.translate({ key: 'app.history', data: { version: version ? normalizeAppVersion(version) : CLEAN_APP_VERSION } }));
}

async function openWhatsNewLink(version?: string): Promise<void> {
  const majorMinor = version ? getMajorMinorVersion(version) : APP_MAJOR_MINOR_VERSION;
  await openUrl(I18n.translate({ key: 'app.whatsNew', data: { version: majorMinor } }));
}

async function openSourcesLink(): Promise<void> {
  await openUrl(I18n.translate('app.projectSources'));
}

async function openDeveloperLink(): Promise<void> {
  await openUrl(I18n.translate('app.developerLink'));
}

async function openTOS(tosLink: string): Promise<void> {
  await openUrl(tosLink);
}

/* Editics variables */

const ENABLE_EDITICS_VARIABLE = 'PARSEC_APP_ENABLE_EDITICS';

function isEditicsEnabled(): boolean {
  return import.meta.env[ENABLE_EDITICS_VARIABLE] === 'true' || (window as any).TESTING_ENABLE_EDITICS === true;
}

export function ensureHttpsProtocol(server: string): string {
  if (!server.startsWith('http://') && !server.startsWith('https://') && !server.startsWith('parsec3://')) {
    window.nativeAPI.log('warn', `URL ${server} doesn't start with http/https/parsec3, forcing https.`);
    return `https://${server}`;
  }
  return server;
}

export const Env = {
  getStripeApiKey,
  getBmsUrl,
  getDocumentationUserGuideLink,
  getSignUrl,
  getSaasServers,
  getTrialServers,
  isStripeDisabled,
  isSentryDisabled,
  getAccountServer,
  isAccountEnabled,
  isEditicsEnabled,
  isAccountAutoLoginEnabled,
  isCustomBrandingEnabled,
  ensureHttpsProtocol,
  isShamirEnabled,
  Links: {
    openDocumentationLink,
    openDocumentationUserGuideLink,
    openDownloadParsecLink,
    openContactLink,
    openLicenseLink,
    openChangelogLink,
    openSourcesLink,
    openDeveloperLink,
    openTOS,
    openUrl,
    openSequesterDocumentationLink,
    openWhatsNewLink,
  },
};
